import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryState } from './QueryState';
import { roundNumbersIn } from './HabitHistory';
import {
  acceptInvitationById,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  ApiError,
  type Notification,
} from '../api';

/**
 * The bell (§7.4, AC8).
 *
 * The unread count comes back with every write, so the badge updates from the response
 * rather than from a refetch that could race the next poll.
 */

const parseRef = (ref: string | null): { kind: string; id: string } | null => {
  if (!ref) return null;
  const at = ref.indexOf(':');
  return at > 0 ? { kind: ref.slice(0, at), id: ref.slice(at + 1) } : null;
};

/**
 * The invitation a notification is offering, if it is one.
 *
 * Read from the payload rather than from the entity ref: the ref names the *project*, which is
 * what makes the row attributable to the projects family for a narrow token, while the thing
 * there is to act on is the offer.
 */
const invitationIn = (n: Notification): string | null => {
  if (n.type !== 'project.invited') return null;
  const id = (n.payload as { invitationId?: unknown }).invitationId;
  return typeof id === 'string' ? id : null;
};

const ago = (iso: string): string => {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export function NotificationBell({
  onOpenTask,
  onOpenHabits,
  onOpenGoals,
  onOpenParked,
  onJoinedProject,
}: {
  onOpenTask: (id: string) => void;
  /** Tasks, on the parked shelf — where the dormancy digest points. See `follow`. */
  onOpenParked: () => void;
  /** With the habit's id, so the screen can open the one the notification is about. */
  onOpenHabits: (id?: string) => void;
  onOpenGoals: () => void;
  /** A project just became visible to this account — the list it belongs in must be refetched. */
  onJoinedProject: (projectId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  /**
   * Close on a click elsewhere, or on Escape.
   *
   * A popover that only closes by clicking the control that opened it is a popover that
   * stays open: people dismiss things by looking away and clicking the next thing.
   * `pointerdown` rather than `click` so it closes on the way down, before whatever was
   * clicked underneath reacts.
   */
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const bell = useQuery({
    queryKey: ['notifications'],
    queryFn: listNotifications,
    refetchInterval: 60_000,
    // Polls, and renders into the header — so it is not the workspace being busy. See
    // `workspaceLoading` in `App.tsx`.
    meta: { polls: true },
  });

  const read = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  /*
    An invitation is the one notification that is not a pointer at something you can already
    open: until it is accepted the project does not exist for you, so following the link would
    be a navigation into a 404. It carries its own answer instead.

    A failure is shown rather than swallowed — an offer that expired or was withdrawn while the
    panel sat open is exactly the case the report was about, and "nothing happened" is the
    response that started all this.
  */
  const join = useMutation({
    mutationFn: ({ invitationId }: { invitationId: string; notificationId: string }) =>
      acceptInvitationById(invitationId),
    onSuccess: (result, variables) => {
      setJoinError(null);
      read.mutate(variables.notificationId);
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      onJoinedProject(result.projectId);
      setOpen(false);
    },
    onError: (e) => {
      /*
        `not_found` covers two cases the reader is owed a sentence about rather than an id:
        somebody else's offer, and — the one that actually happens — an offer whose project has
        since been deleted, which leaves the notification in the bell after the invitation row
        has gone. The server's own message names the id, which is the right answer to an API
        call and the wrong one to a person.
      */
      if (e instanceof ApiError && e.code === 'not_found') {
        setJoinError('That invitation is no longer there.');
      } else {
        setJoinError(e instanceof ApiError ? e.message : 'That invitation could not be accepted.');
      }
    },
  });
  const readAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // The optimistic count: after marking one read, the server's reply is authoritative,
  // but until it lands the badge should already have dropped.
  const unread = read.data?.unread ?? readAll.data?.unread ?? bell.data?.unread ?? 0;

  const follow = (notification: Notification) => {
    /*
      An offer is answered by pressing it, and not only by the small button beside it.

      This branch is before the `read.mutate` below for the reason the whole report was about.
      `follow()` returns on `entityTitle === null`, and an invitation's is always null — the ref
      names a project the reader cannot open until they accept, so `resolveEntityRef` has no
      branch for it and by design cannot have one. So pressing the title and body, which is the
      entire clickable area of every other notification, marked the offer read and did nothing.
      That dropped the unread badge, which was the one signal anything had arrived.

      Routed through `join` rather than marking read and then joining, so the two affordances
      behave identically on the failure path too: `join.onSuccess` marks it read itself, and an
      offer that was withdrawn while the panel sat open leaves the row unread with
      `joinError` under the header, rather than consumed with an error beside it.
    */
    const offer = invitationIn(notification);
    if (offer !== null) {
      if (!join.isPending) join.mutate({ invitationId: offer, notificationId: notification.id });
      return;
    }

    const target = parseRef(notification.entityRef);
    if (!notification.readAt) read.mutate(notification.id);
    /*
      The dormancy digest is about the shelf, so it opens the shelf.

      Its `entityRef` names one task — the account's oldest parked one — and that is a *key*, not a
      destination: `taskDormancyJob.ts` anchors there so `refreshOrCreateNotification` keeps one row
      while the digest is ignored. Followed as a task ref, "21 tasks went quiet … keep one by putting
      it back" opened the panel for one of the 21 over a list that showed none of them
      (`01a1774e-1814`). So this branch is before the generic one, and before the dead-link check
      too: the anchor having been deleted or put back says nothing about whether the shelf is there.
      Still marked read above — the shelf's own count on Tasks is what keeps the number in sight.
    */
    if (notification.type === 'task.dormant') {
      onOpenParked();
      setOpen(false);
      return;
    }
    if (!target) return;
    // A link whose entity has since gone is dead rather than a navigation into a 404 —
    // the server tells us by leaving entityTitle null.
    if (notification.entityTitle === null) return;
    if (target.kind === 'task') {
      onOpenTask(target.id);
      setOpen(false);
    } else if (target.kind === 'habit') {
      /*
        With the id, which was parsed here and then thrown away.

        All 24 of the health nut's notifications name a habit that needs logging, and tapping one
        consumed it and landed on the Habits screen at scrollY 0 with every card collapsed — an
        eight-thousand-pixel scroll to reach the thing the app had just pointed at. A task
        notification has always been given `onOpenTask(target.id)`.
      */
      onOpenHabits(target.id);
      setOpen(false);
    } else if (target.kind === 'goal') {
      // Added with the goal drift job and missed here, so a goal notification marked itself read
      // and then did nothing — a dead click on the one notification whose whole purpose is to
      // send you somewhere.
      onOpenGoals();
      setOpen(false);
    }
  };

  return (
    <div className="bell-wrap" ref={wrapper}>
      <button
        className={`tab bell${unread > 0 ? ' has-unread' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unread > 0 ? `, ${unread} unread` : ''}`}
        aria-expanded={open}
      >
        <Icon name="bell" />
        {unread > 0 && <span className="bell-badge">{unread}</span>}
      </button>

      {open && (
        <div className="bell-panel card">
          <header className="row between">
            <h3>Notifications</h3>
            {unread > 0 && (
              <button className="link" onClick={() => readAll.mutate()}>
                Mark all read
              </button>
            )}
          </header>

          <QueryState query={bell} />
          {bell.data?.notifications.length === 0 && <p className="muted">Nothing to report.</p>}

          {joinError && <p className="error">{joinError}</p>}

          <ul className="plain bell-list">
            {bell.data?.notifications.map((n) => {
              const invitationId = invitationIn(n);
              return (
                <li key={n.id} className={n.readAt ? 'bell-item read' : 'bell-item'}>
                  <button className="bell-link" onClick={() => follow(n)}>
                    <strong>{n.title}</strong>
                    {/* Rounded here rather than trusting the stored string — see `roundNumbersIn`. */}
                    {n.body && <span className="subtext">{roundNumbersIn(n.body)}</span>}
                    <span className="muted small">
                      {ago(n.createdAt)}
                      {n.entityTitle && ` · ${n.entityTitle}`}
                      {/*
                        An invitation names a project the reader cannot open yet, so the server
                        cannot resolve its title and the usual tail would read "no longer exists"
                        about the very thing being offered.
                      */}
                      {n.entityRef &&
                        n.entityTitle === null &&
                        !invitationId &&
                        ' · no longer exists'}
                    </span>
                  </button>
                  {invitationId && (
                    <button
                      className="join-invite"
                      disabled={join.isPending}
                      onClick={() => join.mutate({ invitationId, notificationId: n.id })}
                    >
                      Join project
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
